import { useCustom } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Boxes, Check, EyeOff, ShieldAlert, Undo2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { FindingRow } from "@/components/Finding";
import {
  boxWord,
  errorMessage,
  findingWord,
  moveWord,
  taskWord,
  thingWord,
} from "@/lib/format";
import { api } from "@/lib/api";
import { pageData } from "@/lib/loaded";
import { DATA_QUERIES } from "@/lib/providers";
import type {
  FbsCheck,
  FbsControl,
  FbsFinding,
  FbsHiddenFinding,
  FbsRecount,
} from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

/**
 * FBS Контроль: сходится ли складская сборка сама с собой.
 *
 * Остальные разделы отвечают на вопросы склада — «сколько их у нас», «какой
 * короб везти первым», «что с этой вещью». Здесь вопрос к самой системе:
 * **не врёт ли учёт о собственной работе.** Задача сборки живёт в одной базе,
 * её переезды — в другой, и расхождение между ними не видно ни одной из
 * сторон поодиночке.
 *
 * Экран рисует ВСЕ правила реестра, включая те, что сегодня ничего не нашли:
 * раздел отвечает и на «что проверено», а не только на «что найдено».
 * Исчезнувший с экрана контроль неотличим от снятого.
 *
 * Формы находки экран не знает и знать не должен: дверь присылает готовые
 * строки, а сюда едут только адреса (номер вещи, номера задач). Поэтому новое
 * правило контроля добавляется строкой в реестре на сервере и появляется
 * здесь само.
 */
export function FbsControlPage() {
  const report = useCustom<FbsControl>({ url: "/api/fbs/control", method: "get" });
  useSessionGuard(report.query.error);

  // Телом считается только payload, в котором ДЕЙСТВИТЕЛЬНО есть массив:
  // `result.data` у Refine существует до ответа пустым объектом, и обращение
  // к вложенным полям на нём роняет экран белым листом (разбор — `lib/loaded.ts`).
  const data = pageData(report.query.data?.data, "checks");
  const error = report.query.error ? errorMessage(report.query.error) : "";

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-auto p-4"
      data-component="FbsControlPage"
      data-annot="fbs контроль"
    >
      <div className="mb-3">
        <h1 className="text-[15px] font-semibold">FBS Контроль</h1>
        <p className="mt-0.5 text-[12.5px] text-fg-muted">
          Сверка задач сборки с перемещениями склада: не разошёлся ли учёт сам
          с собой.
        </p>
      </div>

      {error ? (
        <div
          role="alert"
          data-annot="отказ"
          className="mb-3 flex items-center gap-2 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 py-2 text-[12.5px]"
        >
          <AlertTriangle size={14} className="text-err" />
          {/* Отказ здесь ВАЖНЕЕ, чем в списках: контроль, который не смог
              свериться, обязан молчать вслух, а не показать «ошибок нет». */}
          <span>{error}</span>
          <button
            type="button"
            onClick={() => report.query.refetch()}
            className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
          >
            Повторить
          </button>
        </div>
      ) : null}

      {!data ? (
        error ? null : (
          <p className="text-[13px] text-fg-muted">Сверяю…</p>
        )
      ) : (
        <>
          <dl
            data-annot="сводка"
            className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4"
            aria-busy={report.query.isFetching}
          >
            {/* ГЛАВНОЕ ЧИСЛО РАЗДЕЛА, и оно ОДНО: всё показанное требует
                разбора. Второй плитки «всего находок» здесь была бы ровно та
                же цифра — законные случаи в ответ не попадают вовсе. */}
            <Metric
              label="Требуют разбора"
              value={data.found}
              unit={findingWord(data.found)}
              tone={data.found > 0 ? "bad" : "good"}
            />
            {/* Объём проверки стоит рядом с находками: «ошибок нет» без него
                неотличимо от «данных не было». */}
            <Metric
              label="Задач проверено"
              value={data.scanned.tasks}
              unit={taskWord(data.scanned.tasks)}
              note={`с серией ${data.scanned.with_serial}`}
            />
            <Metric
              label="Перемещений сверено"
              value={data.scanned.moves}
              unit={moveWord(data.scanned.moves)}
              note={
                data.scanned.moves_missing > 0
                  ? `${data.scanned.moves_missing} не нашлись в CRM`
                  : ""
              }
              tone={data.scanned.moves_missing > 0 ? "bad" : "plain"}
            />
            {/* Столы сборщиков — объём правила «зависла на столе». Ноль здесь
                значит «столы пусты», и это единственное, что отличает его от
                непрочитанного склада: находок в обоих случаях ноль. */}
            <Metric
              label="На столах подбора"
              value={data.scanned.on_pick}
              unit={thingWord(data.scanned.on_pick)}
              note={data.scanned.on_pick === 0 ? "столы пусты" : ""}
            />
          </dl>

          <div className="flex flex-col gap-3">
            {data.checks.map((check) => (
              <CheckCard key={check.code} check={check} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Число сводки. `tone` подкрашивает только то, что означает работу. */
function Metric({
  label,
  value,
  unit,
  note,
  tone = "plain",
}: {
  label: string;
  value: number;
  unit: string;
  note?: string;
  tone?: "plain" | "good" | "bad";
}) {
  const color =
    tone === "bad" ? "text-err" : tone === "good" ? "text-ok" : "text-fg";
  return (
    <div
      data-annot={label}
      className="rounded-[var(--radius-ui)] border border-border px-3 py-2"
    >
      <dt className="text-[11.5px] uppercase tracking-wide text-fg-muted">
        {label}
      </dt>
      <dd className={`mt-0.5 text-[18px] font-semibold leading-none ${color}`}>
        {value}
        <span className="ml-1 text-[12px] font-normal text-fg-muted">{unit}</span>
      </dd>
      {note ? <p className="mt-1 text-[11.5px] text-fg-muted">{note}</p> : null}
    </div>
  );
}

/**
 * Один контроль: чем он занят, что нашёл и что с этим делать.
 *
 * Контроль без находок печатается тоже — строкой «сходится». Он и есть ответ:
 * проверили и расхождений нет.
 */
function CheckCard({ check }: { check: FbsCheck }) {
  const clean = check.found === 0;
  // `?? []`: экран может обновиться раньше сервиса, и поля в ответе ещё нет.
  const hidden = check.hidden ?? [];
  return (
    <section
      data-annot={`контроль ${check.code}`}
      className="rounded-[var(--radius-ui)] border border-border"
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        {clean ? (
          <Check size={14} className="text-ok" />
        ) : (
          <ShieldAlert size={14} className="text-err" />
        )}
        <h2 className="text-[13.5px] font-medium">{check.title}</h2>
        <span
          className={
            "rounded-full px-2 py-0.5 text-[11.5px] " +
            (clean ? "bg-ok-bg text-ok" : "bg-err-bg text-err")
          }
        >
          {clean ? "сходится" : `${check.found} ${findingWord(check.found)}`}
        </span>
        {/* Вопрос правила стоит РЯДОМ с числом: «0» сам по себе не говорит,
            что именно проверено, и неотличим от неработающего контроля. */}
        <p className="w-full text-[12px] text-fg-muted">{check.question}</p>
      </header>

      {clean || check.recount === undefined ? null : (
        <RecountSummary recount={check.recount} />
      )}

      {clean ? null : (
        <ul className="divide-y divide-border">
          {check.items.map((item, i) => (
            <FindingRow key={`${check.code}-${item.serial ?? "x"}-${i}`} item={item}>
              {check.recount ? (
                <RecountLine recount={check.recount} serial={item.serial} />
              ) : null}
              <AckAction code={check.code} item={item} />
            </FindingRow>
          ))}
        </ul>
      )}

      {/* Разобранное не исчезает молча: скрытая находка без следа была бы
          неотличима от той, которой не было. Свёрнуто — работы оно не требует. */}
      {hidden.length > 0 ? (
        <details data-annot="скрыто после разбора" className="border-t border-border">
          <summary className="cursor-pointer px-3 py-1.5 text-[12px] text-fg-muted hover:text-fg">
            Скрыто после разбора: {hidden.length}
          </summary>
          <ul className="divide-y divide-border opacity-80">
            {hidden.map((item, i) => (
              <FindingRow key={`${check.code}-h-${item.serial ?? "x"}-${i}`} item={item}>
                <HiddenNote code={check.code} item={item} />
              </FindingRow>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

/** Отправить отметку и перечитать ДАННЫЕ раздела (не сессию — `DATA_QUERIES`). */
function useAckCall() {
  const queries = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (call: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await call();
      await queries.invalidateQueries({ queryKey: DATA_QUERIES });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

/**
 * «Разобрано»: находку проверили (например, пересчитали короб) — убрать её
 * с экрана. Заметка необязательна, но это единственный след того, ЧТО проверили.
 */
function AckAction({ code, item }: { code: string; item: FbsFinding }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const { busy, error, run } = useAckCall();
  const id = `ack-${code}-${item.serial ?? "x"}-${item.tasks.join("-")}`;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1.5 inline-flex items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
      >
        <EyeOff size={12} />
        Разобрано
      </button>
    );
  }
  return (
    <form
      className="mt-1.5 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void run(() =>
          api.post("/api/fbs/control/ack", {
            code,
            serial: item.serial,
            tasks: item.tasks,
            note,
          }),
        );
      }}
    >
      <label htmlFor={id} className="sr-only">
        Что проверили
      </label>
      <input
        id={id}
        autoFocus
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Что проверили: например, короб 11077 пересчитан, сошлось"
        className="h-7 min-w-0 flex-1 basis-64 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px]"
      />
      <button
        type="submit"
        disabled={busy}
        className="h-7 rounded-[var(--radius-ui)] border border-border bg-bg-hover px-2 text-[12px] disabled:opacity-60"
      >
        {busy ? "Скрываю…" : "Скрыть"}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="h-7 px-2 text-[12px] text-fg-muted hover:text-fg"
      >
        Отмена
      </button>
      {error ? <span role="alert" className="w-full text-[12px] text-err">{error}</span> : null}
    </form>
  );
}

/** Кто, когда и почему скрыл — плюс кнопка вернуть находку на экран. */
function HiddenNote({ code, item }: { code: string; item: FbsHiddenFinding }) {
  const { busy, error, run } = useAckCall();
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] text-fg-muted">
      <span>
        Разобрано: {item.by} · {item.at.slice(0, 16)}
        {item.note ? ` · ${item.note}` : ""}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() =>
          void run(() =>
            api.delete("/api/fbs/control/ack", {
              code,
              serial: item.serial,
              tasks: item.tasks,
            }),
          )
        }
        className="inline-flex items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2 py-0.5 hover:bg-bg-hover hover:text-fg disabled:opacity-60"
      >
        <Undo2 size={12} />
        {busy ? "Возвращаю…" : "Вернуть на экран"}
      </button>
      {error ? <span role="alert" className="w-full text-err">{error}</span> : null}
    </div>
  );
}

/**
 * Короба к пересчёту — СВОДНО, без повторов (задача владельца 23.09.26).
 *
 * Серия записана в две отгрузки — где-то у этого баркода может не хватать
 * вещи. Пересчитываются только КОРОБА (не зал) и только в салоне, где шёл
 * подбор. Один короб на несколько находок стоит здесь один раз.
 */
function RecountSummary({ recount }: { recount: FbsRecount | null }) {
  if (recount === null) {
    return (
      <p
        role="alert"
        data-annot="к пересчёту"
        className="flex items-center gap-2 border-b border-border bg-err-bg px-3 py-2 text-[12.5px]"
      >
        <AlertTriangle size={14} className="text-err" />
        Короба к пересчёту не прочитаны: CRM не ответила. Это не «пересчитывать
        нечего».
      </p>
    );
  }
  return (
    <div data-annot="к пересчёту" className="border-b border-border bg-bg-hover/40 px-3 py-2">
      <h3 className="flex items-center gap-1.5 text-[12.5px] font-medium">
        <Boxes size={14} />
        К пересчёту: {recount.boxes.length === 0 ? "коробов нет" : `${recount.boxes.length} ${boxWord(recount.boxes.length)}`}
      </h3>
      {recount.boxes.length === 0 ? (
        <p className="mt-0.5 text-[12px] text-fg-muted">
          Баркодов этих находок нет в коробах салонов, где шёл подбор, —
          пересчитывать нечего.
        </p>
      ) : (
        <ul className="mt-1 flex flex-col gap-1">
          {recount.boxes.map((box) => (
            <li key={box.place_id} className="flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
              <Link
                to={`/place/${box.place_id}`}
                className="font-medium text-accent-text hover:underline"
              >
                {box.name}
              </Link>
              <span className="text-fg-muted">
                {box.city} · всего по учёту {box.total} шт
              </span>
              <span className="text-fg-muted">
                ·{" "}
                {box.barcodes
                  .map((one) => `баркод ${one.barcode} — ${one.units} шт`)
                  .join(", ")}
              </span>
              <span className="text-fg-muted">· находки {box.serials.join(", ")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Что это за товар и какие короба пересчитать по ЭТОЙ находке. */
function RecountLine({ recount, serial }: { recount: FbsRecount; serial: number | null }) {
  const item = recount.items.find((one) => one.serial === serial);
  if (!item) return null;
  const card = item.card;
  const boxes = recount.boxes.filter((one) => item.boxes.includes(one.place_id));
  return (
    <div data-annot="товар и короба находки" className="mt-2 flex items-start gap-3">
      {card?.image_url ? (
        <img
          src={card.image_url}
          alt=""
          referrerPolicy="no-referrer"
          className="h-[72px] w-[54px] flex-none rounded-[var(--radius-ui)] border border-border object-cover"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : null}
      <div className="min-w-0 text-[12.5px]">
        {card ? (
          <>
            <p>
              <span className="font-mono">{card.barcode}</span> · размер {card.size}
            </p>
            <p className="text-fg-muted">
              {card.name}
              {card.color ? `, ${card.color}` : ""}
            </p>
          </>
        ) : (
          <p className="text-fg-muted">Товар не заведён на маркетплейс</p>
        )}
        <p className="mt-0.5">
          {boxes.length > 0 ? (
            <>
              Пересчитать:{" "}
              {boxes.map((box, i) => (
                <span key={box.place_id}>
                  {i > 0 ? ", " : ""}
                  <Link to={`/place/${box.place_id}`} className="text-accent-text hover:underline">
                    {box.name}
                  </Link>
                </span>
              ))}
            </>
          ) : (
            <span className="text-fg-muted">
              В коробах{item.cities.length ? ` салона ${item.cities.join(", ")}` : ""} этого
              баркода нет — пересчитывать нечего
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
